import { Tooltip } from "flowbite-react";
import { CircleHelp } from "lucide-react";
import { PURCHASE_POINTS_UNIT } from "@/features/purchasePoints/config/purchasePoints.config";

// The parent grid a BalanceFigure sits in: its own subgrid spans these two
// rows, so every label lines up in one row and every figure in the other,
// whatever their font sizes.
export const BALANCE_FIGURES_ROW = "grid gap-4 justify-around items-start sm:grid-flow-col sm:auto-cols-max sm:grid-rows-2 sm:gap-x-12 sm:gap-y-1";
export const BALANCE_FIGURES_ROW_CENTERED = "grid row-span-3 grid-rows-subgrid justify-items-center items-center";

type BalanceFigureProps = {
  label: string
  points: number | null
  testId: string
  isPrimary?: boolean
  explainer?: string
};

export const BalanceFigure = ({
  label, points, testId, isPrimary = false, explainer,
}: BalanceFigureProps) => (
  // Hands its two rows to the parent grid, so every label shares one row and
  // every figure another, whatever their font sizes.
  <div className="grid gap-1 sm:row-span-2 sm:grid-rows-subgrid sm:items-baseline">
    <span className="inline-flex items-center gap-1.5 text-sm font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
      {label}
      {explainer && (
        // Flowbite keeps a tooltip on one line, and lays it out even hidden:
        // this sentence would stretch the whole page on a phone.
        <Tooltip content={explainer} className="max-w-xs whitespace-normal">
          {/* A bare icon is out of the tab order: the explanation that spares a
              support ticket would only ever reach a mouse. */}
          <button type="button" aria-label={explainer} className="align-middle">
            <CircleHelp size={16} aria-hidden="true" />
          </button>
        </Tooltip>
      )}
    </span>

    <span
      className={isPrimary
        ? "text-3xl font-bold leading-none text-gray-900 dark:text-white"
        : "text-xl font-semibold leading-none text-gray-700 dark:text-gray-300"}
      data-testid={testId}
    >
      {(points ?? 0).toLocaleString("fr-FR")}
      {" "}
      <span className={isPrimary
        ? "text-lg font-semibold text-gray-500 dark:text-gray-400"
        : "text-base font-medium text-gray-500 dark:text-gray-400"}
      >
        {PURCHASE_POINTS_UNIT}
      </span>
    </span>
  </div>
);

BalanceFigure.displayName = "BalanceFigure";
